import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
    GraduationCap,
    ClipboardCheck,
    BarChart3,
    CalendarCheck,
    BookOpenCheck,
    MessageSquare,
    FileBarChart2,
} from "lucide-react";
import { TID } from "../../lib/testIds";

const features = [
    {
        icon: GraduationCap,
        title: "Student Management",
        desc: "Unified records, enrollment, lifecycle and progress in one place.",
    },
    {
        icon: ClipboardCheck,
        title: "Online Assessments",
        desc: "Author, deliver, proctor and auto-grade assessments at scale.",
    },
    {
        icon: BarChart3,
        title: "Analytics",
        desc: "Real-time cohort, learning and operational analytics dashboards.",
    },
    {
        icon: CalendarCheck,
        title: "Attendance",
        desc: "Effortless attendance with smart automation and parent visibility.",
    },
    {
        icon: BookOpenCheck,
        title: "Learning Operations",
        desc: "Curriculum, timetable and resource workflows that run themselves.",
    },
    {
        icon: MessageSquare,
        title: "Communication",
        desc: "Multi-channel messaging — students, faculty, parents, admins.",
    },
    {
        icon: FileBarChart2,
        title: "Reporting",
        desc: "Beautiful, board-ready reports generated on demand.",
    },
];

const TiltCard = ({ Icon, title, desc, idx }) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rx = useSpring(useTransform(y, [-50, 50], [8, -8]), {
        stiffness: 600,
        damping: 25,
    });
    const ry = useSpring(useTransform(x, [-50, 50], [-8, 8]), {
        stiffness: 600,
        damping: 25,
    });

    const onMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        x.set(e.clientX - rect.left - rect.width / 2);
        y.set(e.clientY - rect.top - rect.height / 2);
    };
    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.7,
                delay: idx * 0.05,
                ease: [0.16, 1, 0.3, 1],
            }}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            data-testid={TID.whatIsCard}
            style={{
                rotateX: rx,
                rotateY: ry,
                transformStyle: "preserve-3d",
                transformPerspective: 1000,
            }}
            className="group relative rounded-2xl border border-black/[0.06] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-7 overflow-hidden hover:shadow-[0_8px_32px_rgba(139,92,246,0.12)] hover:border-violet-200 transition-all duration-300
            dark:border-white/[0.06] dark:bg-white/[0.02] dark:shadow-none dark:hover:border-white/15 dark:backdrop-blur-xl"
        >
            {/* hover glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                    background:
                        "radial-gradient(400px circle at var(--mx,50%) var(--my,50%), rgba(139,92,246,0.10), transparent 60%)",
                }}
            />
            <div
                style={{ transform: "translateZ(40px)" }}
                className="relative"
            >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-100 mb-5 dark:from-brand-purple/30 dark:to-brand-blue/20 dark:border-white/10">
                    <Icon size={20} className="text-violet-600 dark:text-white/90" />
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-[#111827] dark:text-white">
                    {title}
                </h3>
                <p className="mt-2 text-sm text-[#6B7280] dark:text-white/55 leading-relaxed">
                    {desc}
                </p>
            </div>
        </motion.div>
    );
};

const WhatIs = () => {
    return (
        <section
            id="what-is"
            data-testid={TID.whatIsSection}
            className="relative py-28 md:py-36 px-6"
        >
            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-3xl"
                >
                    <p className="font-mono text-xs tracking-[0.4em] uppercase text-violet-500 dark:text-brand-blue/80">
                        01 — The Platform
                    </p>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-gradient-brand">
                        What is Lambda Quest?
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-[#6B7280] dark:text-white/55 leading-relaxed">
                        A modern SaaS platform built for coaching institutes,
                        schools, colleges, universities, training organizations
                        and EdTech companies. Lambda Quest streamlines every
                        critical workflow your institution touches — beautifully
                        and at scale.
                    </p>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map((f, i) => (
                        <TiltCard
                            key={f.title}
                            Icon={f.icon}
                            title={f.title}
                            desc={f.desc}
                            idx={i}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default WhatIs;
